<template>
  <v-card
    class="card"
    :disabled="!!loading"
    elevation="0"
  >
    <v-toolbar
      flat
      class="card__toolbar"
    >
      <v-toolbar-title
        :style="{ 'color': appColor.textDark }"
        class="card__title"
      >
        {{ listTitle }}
      </v-toolbar-title>
    </v-toolbar>
    <v-list class="card__list">
      <v-list-item
        v-for="doc in docList"
        :key="doc.id"
        class="pl-5 pr-3"
        :class="{ 'card__item--active': doc.id === selectedId }"
        two-line
        @click="$emit('documentClick', doc.id)"
      >
        <v-list-item-icon class="card__list-icon mr-5">
          <v-icon
            v-if="doc.status === 'success'"
            :color="appColor.success"
          >
            bi-check-square
          </v-icon>
          <v-icon
            v-if="doc.status === 'warn'"
            color="warning"
          >
            bi-exclamation-square
          </v-icon>
          <v-icon
            v-if="doc.status === 'fail'"
            :color="appColor.error"
          >
            bi-x-square
          </v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title :style="{ 'color': appColor.textDark }">
            {{ doc.title }}
          </v-list-item-title>
          <v-list-item-subtitle
            :class="statusVisual(doc.status)?.class"
            :style="{ 'color': `${ statusVisual(doc.status)?.style } !important` }"
          >
            {{ subtitle(doc) }}
          </v-list-item-subtitle>
        </v-list-item-content>
        <v-list-item-icon
          v-if="!certificate"
          class="card__list-icon my-3"
        >
          <v-tooltip
            v-if="doc.docIdToDownload"
            bottom
          >
            <template #activator="{ on }">
              <v-btn
                icon
                :ripple="false"
                :color="appColor.secondary"
                class="mr-1"
                :loading="doc.docIdToDownload === documentLoadingId"
                v-on="on"
                @click.stop="documentDownload(doc.docIdToDownload)"
              >
                <v-icon
                  size="22"
                  :color="appColor.icon"
                >
                  bi-download
                </v-icon>
              </v-btn>
            </template>
            <span>Скачать исходный документ</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template #activator="{ on }">
              <v-btn
                icon
                :ripple="false"
                :color="appColor.secondary"
                :loading="doc.id === loading"
                v-on="on"
                @click.stop="$emit('showPreview', doc)"
              >
                <v-icon :color="appColor.icon">
                  bi-eye
                </v-icon>
              </v-btn>
            </template>
            <span>Просмотр документа</span>
          </v-tooltip>
        </v-list-item-icon>
      </v-list-item>
    </v-list>
    <v-card-actions
      v-if="certificate"
      class="card__actions"
    >
      <v-btn
        class="card__actions__certificate__btn"
        :color="`${appColor.secondary} !important`"
        @click="$emit('resetAll')"
      >
        <v-icon
          left
          class="pr-5"
          size="24"
        >
          bi-box-arrow-left
        </v-icon>
        Завершить
      </v-btn>
    </v-card-actions>
    <v-card-actions
      v-else
      class="card__actions card__actions__signature"
    >
      <v-btn
        class="card__actions__signature__top"
        :style="{ 'color': `${ appColor.textDark } !important` }"
        :loading="reportLoading"
        @click="reportDownload"
      >
        <v-icon
          left
          class="pr-5"
          size="20"
        >
          bi-download
        </v-icon>
        Файл отчета
      </v-btn>
      <div class="card__actions__signature__bottom">
        <v-btn
          class="card__actions__signature__bottom__btn card__actions__signature__bottom__btn--left"
          :color="`${appColor.secondary} !important`"
          @click="$emit('signatureBack')"
        >
          <v-icon
            left
            class="pr-5"
            size="24"
          >
            bi-arrow-left-short
          </v-icon>
          Назад
        </v-btn>
        <v-btn
          class="card__actions__signature__bottom__btn card__actions__signature__bottom__btn--right"
          :color="`${appColor.secondary} !important`"
          @click="$emit('signatureReset')"
        >
          <v-icon
            left
            class="pr-5"
            size="24"
          >
            bi-box-arrow-left
          </v-icon>
          Завершить
        </v-btn>
      </div>
    </v-card-actions>
  </v-card>
</template>

<script>
import { downloadBlob, isArray, isNotEmptyObj } from '@/helpers/helpers'
import { apiGetOriginalDoc, apiGetReport } from '@/api/api'

export default {
  name: 'ResultFileList',

  props: {
    data: {
      type: Array,
      require: true,
      default: () => []
    },
    loading: {
      type: String,
      default: '',
      require: false
    },
    selectedId: {
      type: String,
      default: '',
      require: false
    },
    listTitle: {
      type: String,
      default: 'Список документов',
      require: false
    },
    certificate: {
      type: Boolean,
      default: false,
      require: false
    }
  },

  data () {
    return {
      reportLoading: false,
      documentLoadingId: '',
      appColor: this.$appConfig.color
    }
  },

  computed: {
    docList () {
      if (!isArray(this.data)) {
        return []
      }

      return this.data.map(item => {
        let status
        if (Object.hasOwn(item, 'signatureResults') && item.status === undefined) {
          const sigResultsStatus = item.signatureResults.map(r => r.status)
          if (sigResultsStatus.includes(false) && sigResultsStatus.includes(true)) {
            status = 'warn'
          } else {
            status = sigResultsStatus[0] ? 'success' : 'fail'
          }
        } else {
          status = item.status ? 'success' : 'fail'
        }

        return {
          id: item.id,
          title: item.titleMain,
          docIdToDownload: item.docIdToDownload,
          status
        }
      })
    }
  },

  methods: {
    async documentDownload (docId) {
      this.documentLoadingId = docId
      const response = await apiGetOriginalDoc(docId)
      if (isNotEmptyObj(response)) {
        downloadBlob(response.blob, response.filename)
      }
      this.documentLoadingId = ''
    },

    async reportDownload () {
      this.reportLoading = true
      const response = await apiGetReport()
      if (isNotEmptyObj(response)) {
        downloadBlob(response.blob, response.filename)
      }
      this.reportLoading = false
    },

    subtitle (document) {
      if (this.certificate) {
        return document.status === 'success' ? 'Сертификат действителен' : 'Сертификат недействителен'
      }

      switch (document.status) {
        case 'success':
          return 'Подпись действительна'
        case 'warn':
          return 'Проверка пройдена частично'
        case 'fail':
          return 'Подпись недействительна'
      }
    },

    statusVisual (docStatus) {
      switch (docStatus) {
        case 'success':
          return {
            class: 'success-text',
            style: this.appColor.success
          }
        case 'warn':
          return {
            class: 'warning-text',
            style: '#FBBC05'
          }
        case 'fail':
          return {
            class: 'error-text',
            style: this.appColor.error
          }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .card {
    position: sticky;
    top: 100px;
    border-radius: 8px;

    @media(max-width: $cp-laptop-breakpoint) {
      top: 120px;
    }

    &__toolbar {
      border-bottom: 1px solid $color-divider;
    }

    &__title {
      position: absolute;
      left: 50%;
      top: 50%;
      padding: 0 !important;
      transform: translate(-50%, -50%);
    }

    &__actions {
      display: flex;
      border-top: 1px solid $color-divider;

      &__certificate {
        &__btn {
          flex: 1;
          height: 60px !important;
          border-radius: 0 0 8px 8px;
          color: white !important;

          @media(max-width: $cp-mobile-breakpoint) {
            font-size: 0.75rem;
          }
        }
      }

      &__signature {
        display: block;

        &__top {
          width: 100%;
          height: 60px !important;
          background-color: white !important;
          border-radius: 0;
        }

        &__bottom {
          display: flex;

          &__btn {
            flex: 1;
            height: 60px !important;
            color: white !important;

            &--left {
              border-radius: 0 0 0 8px;
            }

            &--right {
              border-radius: 0 0 8px 0;
            }
          }
        }
      }
    }

    &__list-icon {
      align-self: inherit;
      margin: 0;
    }

    &__list {
      height: 100%;
      max-height: 385px;
      padding: 0;
      overflow-y: auto;
    }

    &__item {
      &--active {
        background-color: $color-accent;
      }
    }
  }

  .success-text {
    color: $color-success;
  }

  .warning-text {
    color: $color-warning;
  }

  .error-text {
    color: $color-error;
  }
</style>
